/**
 * The official 2026 FIFA World Cup Round-of-32 bracket structure.
 *
 * `WORLD_CUP_R32_SLOT_SEEDS[i]` is the group seeding of bracket slot `i` (0–15). Slot order is the
 * LEAF order the mirrored renderer expects: `mirrorBracket` splits the round into left (slots 0–7)
 * and right (slots 8–15), and the connectors pair adjacent slots `(2i, 2i+1)` into Round-of-16
 * child `i`. Ordering the real Round-of-32 fixtures into this slot order (see
 * `knockoutBracketOrder.helper`) is what makes the tree connect the correct games onto the correct
 * side; the later rounds then follow by team-linkage.
 *
 * Source: official bracket (FIFA match numbers 73–88), cross-checked against the live Round-of-16
 * draw — e.g. slot 0 (Winner E) and slot 1 (Winner I) feed the same R16 game (Paraguay vs France),
 * slot 2 (Runner-up A/B) and slot 3 (Winner F) feed Canada vs Morocco, etc.
 *
 * The eight qualifying third-placed teams cannot be pinned to a group until the group stage ends
 * (FIFA's allocation table), so a `third` side stays generic here — it does not affect the tree
 * shape, only which team fills the slot, which the real fixtures resolve.
 */

/** A Round-of-32 entrant, identified by its group-stage finishing seed. */
export type R32SeedRef =
  | { kind: 'winner'; group: string }
  | { kind: 'runnerUp'; group: string }
  | { kind: 'third' };

export interface R32SlotSeed {
  home: R32SeedRef;
  away: R32SeedRef;
}

/** Slot 0 → 15, in bracket leaf order (NOT match-number order and NOT kickoff order). */
export const WORLD_CUP_R32_SLOT_SEEDS: readonly R32SlotSeed[] = [
  { home: { kind: 'winner', group: 'E' }, away: { kind: 'third' } }, //                       0  (M74)
  { home: { kind: 'winner', group: 'I' }, away: { kind: 'third' } }, //                       1  (M77)
  { home: { kind: 'runnerUp', group: 'A' }, away: { kind: 'runnerUp', group: 'B' } }, //      2  (M73)
  { home: { kind: 'winner', group: 'F' }, away: { kind: 'runnerUp', group: 'C' } }, //        3  (M75)
  { home: { kind: 'runnerUp', group: 'K' }, away: { kind: 'runnerUp', group: 'L' } }, //      4  (M83)
  { home: { kind: 'winner', group: 'H' }, away: { kind: 'runnerUp', group: 'J' } }, //        5  (M84)
  { home: { kind: 'winner', group: 'D' }, away: { kind: 'third' } }, //                       6  (M81)
  { home: { kind: 'winner', group: 'G' }, away: { kind: 'third' } }, //                       7  (M82)
  { home: { kind: 'winner', group: 'C' }, away: { kind: 'runnerUp', group: 'F' } }, //        8  (M76)
  { home: { kind: 'runnerUp', group: 'E' }, away: { kind: 'runnerUp', group: 'I' } }, //      9  (M78)
  { home: { kind: 'winner', group: 'A' }, away: { kind: 'third' } }, //                       10 (M79)
  { home: { kind: 'winner', group: 'L' }, away: { kind: 'third' } }, //                       11 (M80)
  { home: { kind: 'winner', group: 'J' }, away: { kind: 'runnerUp', group: 'H' } }, //        12 (M86)
  { home: { kind: 'runnerUp', group: 'D' }, away: { kind: 'runnerUp', group: 'G' } }, //      13 (M88)
  { home: { kind: 'winner', group: 'B' }, away: { kind: 'third' } }, //                       14 (M85)
  { home: { kind: 'winner', group: 'K' }, away: { kind: 'third' } }, //                       15 (M87)
];

/** Number of Round-of-32 slots (16 ties). */
export const WORLD_CUP_R32_SLOT_COUNT = WORLD_CUP_R32_SLOT_SEEDS.length;
